// src/pages/Inscription/EditActeurModalByUser.jsx
import React, { useState, useEffect } from "react";
import { FaSave, FaTimes } from "react-icons/fa";
import Button from "../../components/Button/Button";
import { updateActeur } from "../../services/acteursServices";
import { toast } from "react-toastify";

const EditActeurModalByUser = ({ isOpen, onClose, acteur, refreshList }) => {
  const [acteurData, setActeurData] = useState({
    nom: "",
    prenom: "",
    email: "",
    tel: "",
    adresse: "",
    cp: "",
    commune: "",
    genre: "",
    date_naissance: "",
    club_acteur: "",
    photo_url: "",
    licence_number: "",
    groupe_souhaite: "",
  });

  useEffect(() => {
    if (acteur) {
      setActeurData({
        nom: acteur.nom || "",
        prenom: acteur.prenom || "",
        email: acteur.email || "",
        tel: acteur.tel || "",
        adresse: acteur.adresse || "",
        cp: acteur.cp || "",
        commune: acteur.commune || "",
        genre: acteur.genre || "",
        date_naissance: acteur.date_naissance || "",
        club_acteur: acteur.club_acteur || "",
        photo_url: acteur.photo_url || "",
        licence_number: acteur.licence_number || "",
        groupe_souhaite: acteur.groupe_souhaite || "",
      });
    }
  }, [acteur]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setActeurData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!acteur) return;

    const finalActeurData = {
      ...acteurData,
      photo_url: acteurData.photo_url || "/img/user_avatar.jpg",
    };

    try {
      await updateActeur(acteur.id, finalActeurData);
      toast.success("Acteur mis à jour avec succès");
      refreshList && refreshList();
      onClose();
    } catch (error) {
      console.error("Erreur lors de la mise à jour de l'acteur:", error);
      toast.error("Erreur lors de la mise à jour de l'acteur");
    }
  };

  if (!isOpen) return null;

  const inputClass =
    "w-full rounded-lg border border-slate-300 dark:border-slate-600 bg-white/90 dark:bg-slate-800/80 px-3 py-2.5 text-sm text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary/60 focus:border-primary/70 transition";
  const labelClass =
    "block text-xs font-semibold tracking-wide uppercase text-slate-700 dark:text-slate-200 mb-1.5";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="w-full max-w-2xl mx-4 rounded-2xl bg-white dark:bg-slate-900 border border-slate-200/80 dark:border-slate-700/80 shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200/80 dark:border-slate-700/80 bg-slate-50/80 dark:bg-slate-900/90">
          <h3 className="text-sm md:text-base font-bold uppercase text-slate-900 dark:text-white">
            Modifier un acteur
          </h3>
          <button
            onClick={onClose}
            className="inline-flex items-center justify-center w-8 h-8 rounded-full hover:bg-slate-200/80 dark:hover:bg-slate-700/80 text-slate-600 dark:text-slate-200 transition-colors"
            title="Fermer"
          >
            <FaTimes />
          </button>
        </div>

        {/* Formulaire */}
        <form onSubmit={handleSubmit} className="px-4 py-4 md:py-5 space-y-4">
          {/* Identité */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className={labelClass}>Nom *</label>
              <input
                className={inputClass}
                type="text"
                name="nom"
                value={acteurData.nom}
                onChange={handleChange}
                required
              />
            </div>
            <div>
              <label className={labelClass}>Prénom *</label>
              <input
                className={inputClass}
                type="text"
                name="prenom"
                value={acteurData.prenom}
                onChange={handleChange}
                required
              />
            </div>
          </div>

          {/* Coordonnées */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className={labelClass}>Email</label>
              <input
                className={inputClass}
                type="email"
                name="email"
                value={acteurData.email}
                onChange={handleChange}
              />
            </div>
            <div>
              <label className={labelClass}>Téléphone</label>
              <input
                className={inputClass}
                type="tel"
                name="tel"
                value={acteurData.tel}
                onChange={handleChange}
              />
            </div>
          </div>

          <div>
            <label className={labelClass}>Adresse</label>
            <input
              className={inputClass}
              type="text"
              name="adresse"
              value={acteurData.adresse}
              onChange={handleChange}
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label className={labelClass}>Code postal</label>
              <input
                className={inputClass}
                type="text"
                name="cp"
                value={acteurData.cp}
                onChange={handleChange}
              />
            </div>
            <div className="sm:col-span-2">
              <label className={labelClass}>Commune</label>
              <input
                className={inputClass}
                type="text"
                name="commune"
                value={acteurData.commune}
                onChange={handleChange}
              />
            </div>
          </div>

          {/* Genre + Date */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className={labelClass}>Genre</label>
              <select
                className={inputClass}
                name="genre"
                value={acteurData.genre}
                onChange={handleChange}
              >
                <option value="">Sélectionner</option>
                <option value="Masculin">Masculin</option>
                <option value="Féminin">Féminin</option>
              </select>
            </div>
            <div>
              <label className={labelClass}>Date de naissance</label>
              <input
                className={inputClass}
                type="date"
                name="date_naissance"
                value={acteurData.date_naissance}
                onChange={handleChange}
              />
            </div>
          </div>

          {/* Club / licence / photo */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className={labelClass}>Votre club</label>
              <input
                className={inputClass}
                type="text"
                name="club_acteur"
                value={acteurData.club_acteur}
                onChange={handleChange}
              />
            </div>
            <div>
              <label className={labelClass}>Numéro de licence</label>
              <input
                className={inputClass}
                type="text"
                name="licence_number"
                value={acteurData.licence_number}
                onChange={handleChange}
              />
            </div>
          </div>

          <div>
            <label className={labelClass}>URL de la photo</label>
            <input
              className={inputClass}
              type="url"
              name="photo_url"
              value={acteurData.photo_url}
              onChange={handleChange}
            />
          </div>

          <div>
            <label className={labelClass}>Groupes souhaités</label>
            <input
              className={inputClass}
              type="text"
              name="groupe_souhaite"
              value={acteurData.groupe_souhaite}
              onChange={handleChange}
            />
          </div>

          {/* Actions */}
          <div className="flex justify-center gap-3 pt-2 pb-1">
            <Button
              text="Mettre à jour"
              icon={FaSave}
              type="submit"
              className="gap-2"
            />
            <Button
              text="Fermer"
              icon={FaTimes}
              type="button"
              onClick={onClose}
              className="gap-2 bg-slate-200 hover:bg-slate-300 text-slate-800 dark:bg-slate-700 dark:hover:bg-slate-600 dark:text-white border-none"
            />
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditActeurModalByUser;
